/**
 * Paying the RAD: which assets are liquidated for it, and what the balance sheet looks like afterwards.
 *
 * THE one implementation. It existed twice for most of this codebase's life — in the frontend's
 * `strategy/StrategyIntegrationUtils.ts`, where it was written, and as a verbatim port inside the
 * server's `suggestionEngine/agedCare.ts` — with each copy reaching for its own build of the funding
 * helpers underneath. Two copies of an arithmetic that decides what a client is told their strategy
 * costs is one copy too many, and they HAD drifted: one produced a `NaN` balance where the other
 * produced a number, for an asset carrying no recorded value. Both now import this.
 *
 * It lives in `shared/` rather than in either caller because that is the only place both can reach.
 * The server's suggestion engine and the frontend's Support-at-Home funding panels both need it, and
 * the frontend cannot import the server's copy without dragging the whole suggestion engine into a
 * browser bundle.
 *
 * A NOTE ON WHEN THIS HAPPENS, because the function itself cannot say it: this is applied to a
 * fact-find BEFORE any projection runs, so the assets leave the balance sheet at the projection's
 * origin. That is wrong whenever care starts later than the run does — a client entering in nine
 * months does not sell their shares today — and it is why `strategyOptions.careStartDate` can defer
 * the CARE COSTS but not yet the payment that funds them. Making this a dated event inside the engine
 * is the outstanding piece of work; see the note on `radPlugin.beforeProjection`.
 */

import { findAssetInFactFind, parseRadFundingAssetKey, resolveRadFundingEntry } from './entityFundingUtils.ts';
import { markAbpFundedAcquisitionAfterLiquidation } from '../abpProjectionUtils.ts';
import type { FactFind, FactFindAsset } from '../factFindTypes.ts';

/** One liquidation entry: `{ id, amount }`, keyed by asset type in the "grouped" RAD funding shape. */
export interface RadFundingLiquidation {
    id: string | number;
    amount: number;
}

/** RAD funding as saved by the suggestion engine: `{ [assetType]: RadFundingLiquidation | RadFundingLiquidation[] }`. */
export type GroupedRadFunding = Record<string, RadFundingLiquidation | RadFundingLiquidation[] | undefined>;

/**
 * RAD funding as {@link applyRadFundingToFactFind} accepts it. Either:
 *  - "manual key" shape: `{ [ "type-id" ]: amount }` (flat, detected via `parseRadFundingAssetKey`), or
 *  - "grouped" shape: `{ [assetType]: RadFundingLiquidation | RadFundingLiquidation[] }`.
 * The flat variant's value is always a plain number at rest (every producer normalises before storing).
 */
export type RadFunding = Record<string, number | string | RadFundingLiquidation | RadFundingLiquidation[] | undefined>;

/** Applies one liquidation to the fact-find being mutated, returning the GROSS taken and NET raised. */
function liquidateOne(
    originalFactFind: FactFind,
    modifiedFactFind: FactFind,
    assetType: string,
    assetId: string | number | undefined,
    // Deliberately `unknown`: `StrategyOptions.radFunding` is the one figure in that bag documented as
    // reaching the engine inconsistently typed (the RAD slider's producers never agreed), so it is
    // coerced here at the point of use rather than trusted to be a number.
    amount: unknown,
    cgtOverrides: Record<string, unknown>,
): { gross: number; net: number } {
    // A liquidation naming no asset funds nothing. Checked before the lookup rather than left to it,
    // because the helper takes an id it can actually match on.
    if (assetId === undefined) return { gross: 0, net: 0 };
    const located = findAssetInFactFind(modifiedFactFind, assetType, assetId);
    if (!located) return { gross: 0, net: 0 };

    // An asset cannot fund more than it holds. Without this an over-allocation — an adviser typing
    // 500,000 against a 120,000 account, or a saved strategy whose asset has since fallen in value —
    // drove that asset's balance NEGATIVE and credited the full amount to the deposit, inventing money
    // and then carrying the negative through every later year of the projection. Both former copies of
    // this function did it, identically, which is why holding them against each other never found it.
    //
    // Clamped BEFORE the amount is priced, so CGT is struck on the shares actually sold rather than on
    // the larger number that was asked for.
    const available = Math.max(0, Number(located.asset.value) || 0);
    const requested = Math.max(0, Number(amount) || 0);
    const fundable = Math.min(requested, available);
    if (fundable <= 0) return { gross: 0, net: 0 };

    // Gross and net are resolved against the ORIGINAL fact-find, not the one being mutated: a second
    // liquidation of the same asset must be priced off what the client actually held, not off what an
    // earlier entry in the same pass has already taken out of it.
    const { gross, net } = resolveRadFundingEntry(originalFactFind, `${assetType}-${assetId}`, fundable, cgtOverrides);
    if (gross <= 0) return { gross: 0, net: 0 };

    const asset = located.asset;
    // `__originalValue` is a sentinel the means test reads: `assessableMeans.ts` infers a pre-2015 ABP's
    // commutation from the gap between it and the current value when `strategyOptions.radFunding` does
    // not spell one out. Written before the FIRST subtraction and kept through any later ones (the
    // day-of-sale top-up can draw the same asset twice), and never cleared.
    if (asset.__originalValue === undefined) asset.__originalValue = asset.value;
    // `|| 0` rather than `parseFloat`, which returns NaN for an asset carrying no value and puts that
    // NaN into the balance sheet — from where it reaches net worth and assessable assets without ever
    // announcing itself. This is the one point the two former copies disagreed on.
    asset.value = (asset.value || 0) - gross;
    if (assetType === 'accountBasedPensions') {
        markAbpFundedAcquisitionAfterLiquidation(asset as FactFindAsset, modifiedFactFind, 1);
    }
    return { gross, net };
}

/**
 * Liquidates the nominated assets to pay the RAD, returning a NEW fact-find — the caller's is never
 * touched. The proceeds become a single synthetic `radPaid` asset, which is how the rest of the engine
 * recognises that a deposit has been paid (see `radPlugin`, and `sumRadPaidAssets`).
 *
 * Accepts both funding shapes because both are in the wild: the suggestion engine stores the grouped
 * one, the manual RAD allocator the flat one. Which is in hand is detected rather than declared, since
 * a caller passing the wrong flag would be a silent no-op.
 *
 * `net` rather than gross is what lands in `radPaid`: selling an asset to fund a deposit has to fund
 * its own CGT first, so the deposit is what survives the tax. `cgtOverrides` lets an accountant's own
 * figure replace the estimate, per funding key.
 *
 * The `1` handed to `markAbpFundedAcquisitionAfterLiquidation` is the projection year the liquidation
 * happens in, and it is hardcoded because this runs before any projection — see the module note.
 */
/**
 * Sells the nominated assets IN PLACE, returning the net proceeds — the deposit-free half of
 * {@link applyRadFundingToFactFind}, which is this plus the `radPaid` row.
 *
 * Split out so the projection engine can do the two things on their own dates. Selling assets and
 * paying a deposit are one act only when they happen at the same instant, which is exactly what the
 * combined function assumes: a client who liquidates in October to move in in March holds cash for
 * five months, and that cash is deemed, means-tested and spendable in a way an already-paid deposit is
 * not. See `radPlugin`, which credits these proceeds to the bank on the sale date and pays the deposit
 * out of it on the care date.
 *
 * `pricedAgainst` is the fact-find CGT and net are struck against, defaulting to the one being
 * mutated. A caller holding an un-mutated original should pass it, so that several liquidations in one
 * pass are each priced off what the client actually held rather than off what an earlier entry left.
 */
/** The funding plan flattened to one shape, in plan order — both wire formats normalise to this. */
function normaliseFundingEntries(radFunding: RadFunding): { assetType: string; assetId: string | number; amount: number }[] {
    const entries: { assetType: string; assetId: string | number; amount: number }[] = [];
    const isManualKeyFormat = Object.keys(radFunding).some((key) => parseRadFundingAssetKey(key));
    if (isManualKeyFormat) {
        for (const [assetKey, amount] of Object.entries(radFunding)) {
            const parsed = parseRadFundingAssetKey(assetKey);
            if (!parsed) continue;
            // The manual-key shape carries a bare gross amount here, never the grouped object below.
            entries.push({ assetType: parsed.assetType, assetId: parsed.assetId, amount: Math.max(0, Number(amount) || 0) });
        }
    } else {
        for (const [assetType, liquidations] of Object.entries(radFunding)) {
            // A non-object entry is discarded rather than wrapped: wrapping a bare number yields a
            // liquidation with neither an id nor an amount, which finds no asset and funds nothing —
            // the same outcome, reached by pretending the input was well-formed.
            const liquidationList: RadFundingLiquidation[] = Array.isArray(liquidations)
                ? liquidations
                : liquidations && typeof liquidations === 'object'
                    ? [liquidations as RadFundingLiquidation]
                    : [];
            for (const liquidation of liquidationList) {
                if (liquidation?.id === undefined) continue;
                entries.push({ assetType, assetId: liquidation.id, amount: Math.max(0, Number(liquidation.amount) || 0) });
            }
        }
    }
    return entries;
}

/** One asset's contribution to a day-of-sale top-up (see below). */
export interface RadFundingTopUp {
    assetType: string;
    assetId: string | number;
    gross: number;
}

export interface RadFundingLiquidationResult {
    /** What the deposit receives — gross drawn less any CGT the sales triggered. */
    netProceeds: number;
    /** The plan's total, and what was actually taken from the balance sheet. */
    grossRequested: number;
    grossDrawn: number;
    /** Day-of-sale top-ups: extra draws from PLANNED assets covering balances that had moved. */
    topUps: RadFundingTopUp[];
    /** What even the planned assets could not cover — a genuine funding failure, zero in the ordinary case. */
    grossShortfall: number;
}

/** As {@link liquidateRadFundingAssets}, returning the full accounting the caller may narrate from. */
export function liquidateRadFundingAssetsDetailed(
    factFind: FactFind,
    radFunding: RadFunding | null | undefined,
    cgtOverrides: Record<string, unknown> = {},
    pricedAgainst: FactFind = factFind,
): RadFundingLiquidationResult {
    if (!radFunding) return { netProceeds: 0, grossRequested: 0, grossDrawn: 0, topUps: [], grossShortfall: 0 };

    const entries = normaliseFundingEntries(radFunding);
    let netProceeds = 0;
    let grossDrawn = 0;
    const grossRequested = entries.reduce((sum, e) => sum + e.amount, 0);

    for (const entry of entries) {
        const drawn = liquidateOne(pricedAgainst, factFind, entry.assetType, entry.assetId, entry.amount, cgtOverrides);
        netProceeds += drawn.net;
        grossDrawn += drawn.gross;
    }

    // DAY-OF-SALE TOP-UP (adviser ruling, 2026-08-19). A funding plan is struck against the
    // balances of the day it is MADE, and a dated sale happens later — pension drawdowns and
    // market movement mean an asset can hold less on the day than the plan allocated ($116 on a
    // $480,000 deposit, in the case that surfaced this). Any adviser would take the difference
    // from the next nominated asset without a second thought, so the engine now does: the
    // remainder is drawn from the SAME assets the plan nominated, in plan order, from whatever
    // they still hold. Assets the plan marked KEPT are never touched — the plan names who funds
    // the deposit; drift only re-weights among them. What survives after that is a genuine
    // shortfall: the nominated assets themselves are exhausted, which is worth a loud sentence.
    const topUps: RadFundingTopUp[] = [];
    let remaining = grossRequested - grossDrawn;
    if (remaining > 0.5) {
        for (const entry of entries) {
            if (remaining <= 0.5) break;
            const drawn = liquidateOne(pricedAgainst, factFind, entry.assetType, entry.assetId, remaining, cgtOverrides);
            if (drawn.gross <= 0) continue;
            netProceeds += drawn.net;
            grossDrawn += drawn.gross;
            remaining -= drawn.gross;
            topUps.push({ assetType: entry.assetType, assetId: entry.assetId, gross: drawn.gross });
        }
    }

    return { netProceeds, grossRequested, grossDrawn, topUps, grossShortfall: Math.max(0, remaining) };
}

export function liquidateRadFundingAssets(
    factFind: FactFind,
    radFunding: RadFunding | null | undefined,
    cgtOverrides: Record<string, unknown> = {},
    pricedAgainst: FactFind = factFind,
): number {
    return liquidateRadFundingAssetsDetailed(factFind, radFunding, cgtOverrides, pricedAgainst).netProceeds;
}

export function applyRadFundingToFactFind(factFind: FactFind, radFunding: RadFunding | null | undefined, cgtOverrides: Record<string, unknown> = {}): FactFind {
    if (!radFunding) return structuredClone(factFind);

    const modifiedFactFind = structuredClone(factFind);
    // Priced against the caller's original, which `structuredClone` has just detached from the copy
    // being mutated — the same "value the client actually held" the in-place helper is documented to use.
    const totalRadPaidNet = liquidateRadFundingAssets(modifiedFactFind, radFunding, cgtOverrides, factFind);

    if (totalRadPaidNet > 0) {
        if (!modifiedFactFind.assets) modifiedFactFind.assets = {};
        modifiedFactFind.assets.radPaid = [{ id: 'radPaid', description: 'RAD Paid', value: totalRadPaidNet, income: 0, growthRate: 0 }];
    }

    return modifiedFactFind;
}
